import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import PageHeader from '../../components/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Snowflake, Radio, CloudSun, Loader2, RefreshCw, Info, MapPin, ArrowLeft } from 'lucide-react';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import chillUnitService, {
  type ChillModel,
  type ChillSeries,
  type ChillOverview,
  type ChillOverviewItem,
} from '../../services/chillUnits';
import '../../styles/GlobalPageStyles.css';
import { toNumOr } from '@/utils/numeric';

Chart.register(...registerables);

// Per-model display metadata. `cum` picks the cumulative field off a ChillDay.
const MODELS: Record<ChillModel, {
  label: string;
  short: string;
  unit: string;
  color: string;
  blurb: string;
}> = {
  dynamic: {
    label: 'Dynamic Model',
    short: 'Chill portions',
    unit: 'CP',
    color: '#7c3aed',
    blurb: 'The scientific standard (Fishman). Chill accumulates best around 6 °C; very cold and warm hours add little, and warm spells can undo recent chill before it is banked.',
  },
  utah: {
    label: 'Utah Model',
    short: 'Chill units',
    unit: 'CU',
    color: '#0284c7',
    blurb: 'Weighted hourly bands. Hours near 2.5–9 °C count fully; warm hours subtract, so the total can fall during warm spells.',
  },
  chilling: {
    label: 'Chilling Hours',
    short: 'Chilling hours',
    unit: 'h',
    color: '#0ea5e9',
    blurb: 'The simplest model — a straight count of hours between 0 °C and 7.2 °C.',
  },
};

function cumFor(model: ChillModel, d: ChillSeries['series'][number]): number {
  if (model === 'dynamic') return toNumOr(d.chill_portions_cum);
  if (model === 'utah') return toNumOr(d.utah_units_cum);
  return toNumOr(d.chilling_hours_cum);
}

function totalFor(model: ChillModel, s: NonNullable<ChillOverviewItem['summary']>): number {
  if (model === 'dynamic') return s.chill_portions;
  if (model === 'utah') return s.utah_units;
  return s.chilling_hours;
}

function fmt(n: number, digits = 1): string {
  return Number(n).toFixed(digits);
}

// -----------------------------------------------------------------------------
// Cumulative chill chart
// -----------------------------------------------------------------------------
function ChillChart({ series, model, tz }: { series: ChillSeries['series']; model: ChillModel; tz: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    if (chartRef.current) chartRef.current.destroy();

    const meta = MODELS[model];
    const isMobile = window.innerWidth < 640;
    const labels = series.map((d) => formatLocalDateInTz(d.local_date, tz, { month: 'short', day: 'numeric' }));

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          label: `${meta.short} (cumulative)`,
          data: series.map((d) => cumFor(model, d)),
          borderColor: meta.color,
          backgroundColor: `${meta.color}22`,
          fill: true,
          tension: 0.2,
          pointRadius: 0,
          borderWidth: 2,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        scales: {
          x: { ticks: { display: !isMobile, maxTicksLimit: 8, autoSkip: true } },
          y: { beginAtZero: true, title: { display: !isMobile, text: meta.unit } },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (item) => `${meta.short}: ${fmt(Number(item.raw), 1)} ${meta.unit}`,
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    };
  }, [series, model, tz]);

  return <div className="h-72"><canvas ref={canvasRef} /></div>;
}

// -----------------------------------------------------------------------------
// ChillUnitsPage
// -----------------------------------------------------------------------------
export default function ChillUnitsPage() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [searchParams, setSearchParams] = useSearchParams();

  const [overview, setOverview] = useState<ChillOverview | null>(null);
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);
  const [season, setSeason] = useState<number | null>(null);
  const [model, setModel] = useState<ChillModel>('dynamic');
  const [series, setSeries] = useState<ChillSeries | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const farmParam = searchParams.get('farm');

  // Load the overview (farm list + per-farm current totals) for the season.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingOverview(true);
      const ov = await chillUnitService.getOverview(season ?? undefined);
      if (cancelled) return;
      setOverview(ov);
      if (ov) {
        if (season == null) setSeason(ov.season_year);
        // Seed selection: ?farm= param, else first farm with data, else first farm.
        if (selectedFarmId == null) {
          const paramId = farmParam ? Number(farmParam) : null;
          const byParam = paramId != null ? ov.items.find((i) => i.farm_id === paramId) : null;
          const withData = ov.items.find((i) => i.summary && i.summary.days_total > 0);
          const pick = byParam || withData || ov.items[0] || null;
          if (pick) setSelectedFarmId(pick.farm_id);
        }
      }
      setLoadingOverview(false);
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  // Load the daily series whenever the selected farm or season changes.
  const loadSeries = useCallback(async (refresh = false) => {
    if (selectedFarmId == null) return;
    if (refresh) setRefreshing(true); else setLoadingSeries(true);
    const data = await chillUnitService.getFarm(selectedFarmId, {
      season: season ?? undefined,
      refresh,
    });
    setSeries(data);
    setRefreshing(false);
    setLoadingSeries(false);
  }, [selectedFarmId, season]);

  useEffect(() => {
    loadSeries(false);
  }, [loadSeries]);

  const selectedItem = useMemo(
    () => overview?.items.find((i) => i.farm_id === selectedFarmId) || null,
    [overview, selectedFarmId],
  );

  const seasonOptions = useMemo(() => {
    const base = season ?? new Date().getFullYear();
    return [base, base - 1, base - 2, base - 3];
  }, [season]);

  const onSelectFarm = (id: number) => {
    setSelectedFarmId(id);
    setSeries(null);
    const next = new URLSearchParams(searchParams);
    next.set('farm', String(id));
    setSearchParams(next, { replace: true });
  };

  const modelMeta = MODELS[model];

  // --- Render ---
  if (loadingOverview && !overview) {
    return (
      <>
        <PageHeader title="Winter chill" />
        <div className="max-w-6xl mx-auto p-6">
          <div className="flex items-center gap-2 text-neutral-500 mt-6">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading…
          </div>
        </div>
      </>
    );
  }

  const items = overview?.items ?? [];
  const seasonLabel = series
    ? `${formatLocalDateInTz(series.meta.season_start, orgTz, { day: 'numeric', month: 'short' })} – ${formatLocalDateInTz(series.meta.season_end, orgTz, { day: 'numeric', month: 'short', year: 'numeric' })}`
    : null;

  return (
    <>
      <PageHeader
        title="Winter chill"
        subtitle={seasonLabel ? `Season ${season} · ${seasonLabel}` : 'Chill accumulation for deciduous fruit'}
      />
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Controls: back to weather, farm picker, season picker */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => navigate('/weather')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
          >
            <ArrowLeft className="h-4 w-4" /> Weather
          </button>

          <label className="flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4 text-neutral-500" />
            <select
              className="rounded-lg border border-neutral-200 px-3 py-2 text-sm"
              value={selectedFarmId ?? ''}
              onChange={(e) => onSelectFarm(Number(e.target.value))}
            >
              {items.map((i) => (
                <option key={i.farm_id} value={i.farm_id}>
                  {i.farm_name}{i.station_id ? ' · station' : ' · modelled'}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <span className="text-neutral-500">Season</span>
            <select
              className="rounded-lg border border-neutral-200 px-3 py-2 text-sm"
              value={season ?? ''}
              onChange={(e) => { setSeason(Number(e.target.value)); setSeries(null); }}
            >
              {seasonOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>

          <button
            onClick={() => loadSeries(true)}
            disabled={refreshing || selectedFarmId == null}
            title="Recompute from the latest temperature data"
            className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-neutral-100 text-neutral-600 hover:bg-neutral-200 disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Source badge for the selected farm */}
        {selectedItem && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {selectedItem.source === 'station' ? (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">
                <Radio className="h-3.5 w-3.5" /> On-farm station{selectedItem.station_name ? ` · ${selectedItem.station_name}` : ''}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sky-50 text-sky-700">
                <CloudSun className="h-3.5 w-3.5" /> Modelled from Open-Meteo (no station)
              </span>
            )}
            {series && series.summary.days_modelled > 0 && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
                <Info className="h-3.5 w-3.5" />
                {series.summary.days_modelled} of {series.summary.days_total} days include gap-filled hours
              </span>
            )}
            {series?.meta.through_date && (
              <span className="text-neutral-500">
                through {formatLocalDateInTz(series.meta.through_date, orgTz, { day: 'numeric', month: 'short' })}
              </span>
            )}
          </div>
        )}

        {/* Current-season totals — all three models, selected one highlighted */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(Object.keys(MODELS) as ChillModel[]).map((m) => {
            const meta = MODELS[m];
            const value = series && series.series.length
              ? cumFor(m, series.series[series.series.length - 1])
              : selectedItem?.summary
                ? totalFor(m, selectedItem.summary)
                : null;
            const active = m === model;
            return (
              <button
                key={m}
                onClick={() => setModel(m)}
                className={`text-left rounded-xl p-4 border transition ${
                active ? 'border-transparent ring-2 ring-offset-1' : 'border-neutral-200 hover:border-neutral-300'
              }`}
                style={active ? { backgroundColor: `${meta.color}12`, boxShadow: `0 0 0 2px ${meta.color}` } : undefined}
              >
                <div className="flex items-center gap-2 text-xs font-medium" style={{ color: meta.color }}>
                  <Snowflake className="h-4 w-4" /> {meta.short}
                </div>
                <div className="mt-1 text-3xl font-bold text-neutral-900">
                  {value == null ? '—' : fmt(value, m === 'chilling' ? 0 : 1)}
                  <span className="text-sm font-medium ml-1 text-neutral-500">{meta.unit}</span>
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">{meta.label}</div>
              </button>
            );
          })}
        </div>

        {/* Chart */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Snowflake className="h-5 w-5" style={{ color: modelMeta.color }} />
              {modelMeta.short} · cumulative
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingSeries && !series && (
              <div className="flex items-center gap-2 text-sm text-neutral-500 h-72">
                <Loader2 className="h-4 w-4 animate-spin" /> Computing chill accumulation…
              </div>
            )}
            {!loadingSeries && series && series.series.length === 0 && (
              <div className="text-sm text-neutral-500 h-72 flex items-center">
                No chill data for this season yet — accumulation starts on {series.meta.season_start}.
              </div>
            )}
            {series && series.series.length > 0 && (
              <ChillChart series={series.series} model={model} tz={orgTz} />
            )}
            <p className="mt-3 text-xs text-neutral-500 leading-relaxed">{modelMeta.blurb}</p>
          </CardContent>
        </Card>

        {/* All-farm overview grid */}
        {items.length > 1 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">All farms · {modelMeta.short} to date</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {items.map((i) => {
                  const value = i.summary ? totalFor(model, i.summary) : null;
                  return (
                    <button
                      key={i.farm_id}
                      onClick={() => onSelectFarm(i.farm_id)}
                      className={`text-left rounded-xl p-3 border ${
                      i.farm_id === selectedFarmId ? 'border-neutral-800' : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                    >
                      <div className="text-sm font-semibold text-neutral-800 truncate">{i.farm_name}</div>
                      <div className="text-xs text-neutral-500 mb-1">
                        {i.source === 'station' ? 'Station' : i.source === 'open_meteo' ? 'Modelled' : i.source === 'error' ? 'Error' : 'No location'}
                      </div>
                      <div className="text-xl font-bold text-neutral-900">
                        {value == null ? '—' : fmt(value, model === 'chilling' ? 0 : 1)}
                        <span className="text-xs font-medium ml-1 text-neutral-500">{modelMeta.unit}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}
